import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
} from '@mui/material';
import EmptyState from './EmptyState';

export interface Column<T> {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  render: (row: T) => React.ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  onSort?: (columnId: string) => void;
  sortKey?: string;
  sortDir?: 'asc' | 'desc';
  getRowId: (row: T) => string;
}

function DataTable<T>({
  columns,
  rows,
  loading,
  emptyTitle = 'No records found',
  emptyDescription = 'Try adjusting your filters or search.',
  onSort,
  sortKey,
  sortDir,
  getRowId,
}: DataTableProps<T>) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!rows.length) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ borderRadius: 2, boxShadow: 'none', overflowX: 'auto' }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.id}
                align={col.align || 'left'}
                style={{ minWidth: col.minWidth }}
                onClick={() => col.sortable && onSort?.(col.id)}
                sx={{ cursor: col.sortable ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
              >
                {col.label}
                {col.sortable && sortKey === col.id ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={getRowId(row)} hover>
              {columns.map((col) => (
                <TableCell key={col.id} align={col.align || 'left'}>
                  {col.render(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default DataTable;
